import type { MediaMetadata } from '@/types/storage'
import type { AudioItem, ImageItem, TextItem, TimelineItem } from '@/types/timeline'
import { getMedia } from '@/infrastructure/storage'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import { useTimelineStore } from '@/features/editor/deps/timeline-store'
import { useProjectStore } from '@/features/export/deps/projects'
import { useItemsStore, useKeyframesStore } from '@/features/editor/deps/timeline-store'
import { EFFECT_PRESETS } from '@/types/effects'
import {
  canAddTransition,
  findCompatibleTrackForItemType,
  findNearestAvailableSpace,
  timelineToSourceFrames,
} from '@/features/editor/deps/timeline-utils'
import { createClassicTrack } from '@/features/timeline/utils/classic-tracks'
import { buildDroppedMediaTimelineItem } from '@/features/timeline/utils/dropped-media'
import { useMarkersStore } from '@/features/timeline/stores/markers-store'
import { insertFreezeFrame } from '@/features/timeline/stores/actions/item-edit-actions'

export interface AssistantTimelineSummary {
  fps: number
  trackCount: number
  itemCount: number
  transitionCount: number
  totalDurationFrames: number
  totalDurationSeconds: number
  inPoint: number | null
  outPoint: number | null
  tracks: Array<{
    id: string
    name: string
    type: string
    order: number
    itemCount: number
  }>
}

export function summarizeTimeline(): AssistantTimelineSummary {
  const timeline = useTimelineStore.getState()
  const totalDurationFrames = timeline.items.reduce(
    (maxFrame, item) => Math.max(maxFrame, item.from + item.durationInFrames),
    0,
  )

  return {
    fps: timeline.fps,
    trackCount: timeline.tracks.length,
    itemCount: timeline.items.length,
    transitionCount: timeline.transitions.length,
    totalDurationFrames,
    totalDurationSeconds: totalDurationFrames / timeline.fps,
    inPoint: timeline.inPoint,
    outPoint: timeline.outPoint,
    tracks: timeline.tracks.map((track) => ({
      id: track.id,
      name: track.name,
      type: track.kind ?? 'unknown',
      order: track.order,
      itemCount: timeline.items.filter((item) => item.trackId === track.id).length,
    })),
  }
}

export async function insertAudioToTimeline(
  media: MediaMetadata,
  blobUrl?: string,
  options?: {
    volumeDb?: number
  },
): Promise<{ inserted: boolean; audioItemId: string | null }> {
  const timeline = useTimelineStore.getState()
  const resolvedBlobUrl = blobUrl || (await mediaLibraryService.getMediaBlobUrl(media.id))
  if (!resolvedBlobUrl) {
    return { inserted: false, audioItemId: null }
  }
  const targetTrack =
    findCompatibleTrackForItemType({
      tracks: timeline.tracks,
      items: timeline.items,
      itemType: 'audio',
      preferredTrackId: null,
    }) ??
    (() => {
      const bottomOrder = timeline.tracks.reduce(
        (maxOrder, track) => Math.max(maxOrder, track.order),
        -1,
      )
      const nextTrack = createClassicTrack({
        tracks: timeline.tracks,
        kind: 'audio',
        order: bottomOrder + 1,
      })
      timeline.setTracks([...timeline.tracks, nextTrack])
      return nextTrack
    })()

  if (!targetTrack) {
    return { inserted: false, audioItemId: null }
  }

  const sourceFps = media.fps || timeline.fps
  const durationInFrames = Math.max(1, Math.round(media.duration * timeline.fps))
  const sourceDurationFrames = Math.round(media.duration * sourceFps)
  const desiredPosition = 0
  const finalPosition =
    findNearestAvailableSpace(desiredPosition, durationInFrames, targetTrack.id, timeline.items) ??
    desiredPosition

  const audioItemId = crypto.randomUUID()
  const audioItem: AudioItem = {
    id: audioItemId,
    type: 'audio',
    trackId: targetTrack.id,
    from: finalPosition,
    durationInFrames,
    label: media.fileName,
    mediaId: media.id,
    originId: crypto.randomUUID(),
    src: resolvedBlobUrl,
    sourceStart: 0,
    sourceEnd: sourceDurationFrames,
    sourceDuration: sourceDurationFrames,
    sourceFps,
    trimStart: 0,
    trimEnd: 0,
    volume: options?.volumeDb,
  }

  timeline.addItem(audioItem)
  const inserted = useTimelineStore.getState().items.some((item) => item.id === audioItemId)
  return { inserted, audioItemId: inserted ? audioItemId : null }
}

export async function insertLoopingAudioToTimeline(
  media: MediaMetadata,
  blobUrl: string | undefined,
  options: {
    totalDurationSeconds: number
    volumeDb?: number
    placement?: 'existing-compatible-track' | 'new-bottom-track'
  },
): Promise<{ inserted: boolean; audioItemIds: string[] }> {
  const timeline = useTimelineStore.getState()
  const resolvedBlobUrl = blobUrl || (await mediaLibraryService.getMediaBlobUrl(media.id))
  if (!resolvedBlobUrl) {
    return { inserted: false, audioItemIds: [] }
  }
  const targetTrack =
    options.placement === 'new-bottom-track'
      ? (() => {
          const bottomOrder = timeline.tracks.reduce(
            (maxOrder, track) => Math.max(maxOrder, track.order),
            -1,
          )
          const nextTrack = createClassicTrack({
            tracks: timeline.tracks,
            kind: 'audio',
            order: bottomOrder + 1,
          })
          timeline.setTracks([...timeline.tracks, nextTrack])
          return nextTrack
        })()
      : findCompatibleTrackForItemType({
          tracks: timeline.tracks,
          items: timeline.items,
          itemType: 'audio',
          preferredTrackId: null,
        })

  if (!targetTrack) {
    return { inserted: false, audioItemIds: [] }
  }

  const sourceFps = media.fps || timeline.fps
  const singleDurationFrames = Math.max(1, Math.round(media.duration * timeline.fps))
  const sourceDurationFrames = Math.round(media.duration * sourceFps)
  const totalDurationFrames = Math.max(
    singleDurationFrames,
    Math.round(Math.max(options.totalDurationSeconds, 0) * timeline.fps),
  )
  const desiredPosition = 0
  const finalPosition =
    findNearestAvailableSpace(
      desiredPosition,
      totalDurationFrames,
      targetTrack.id,
      timeline.items,
    ) ?? desiredPosition

  const createdIds: string[] = []
  let remainingFrames = totalDurationFrames
  let nextStartFrame = finalPosition

  while (remainingFrames > 0) {
    const durationInFrames = Math.min(singleDurationFrames, remainingFrames)
    const sourceFramesUsed = Math.min(
      sourceDurationFrames,
      timelineToSourceFrames(durationInFrames, 1, timeline.fps, sourceFps),
    )
    const audioItemId = crypto.randomUUID()
    const audioItem: AudioItem = {
      id: audioItemId,
      type: 'audio',
      trackId: targetTrack.id,
      from: nextStartFrame,
      durationInFrames,
      label: media.fileName,
      mediaId: media.id,
      originId: crypto.randomUUID(),
      src: resolvedBlobUrl,
      sourceStart: 0,
      sourceEnd: sourceFramesUsed,
      sourceDuration: sourceDurationFrames,
      sourceFps,
      trimStart: 0,
      trimEnd: Math.max(0, sourceDurationFrames - sourceFramesUsed),
      volume: options.volumeDb,
    }

    timeline.addItem(audioItem)
    createdIds.push(audioItemId)
    remainingFrames -= durationInFrames
    nextStartFrame += durationInFrames
  }

  const insertedIds = createdIds.filter((itemId) =>
    useTimelineStore.getState().items.some((item) => item.id === itemId),
  )
  return {
    inserted: insertedIds.length === createdIds.length && insertedIds.length > 0,
    audioItemIds: insertedIds,
  }
}

export async function insertReferenceMediaSequenceToTimeline(
  mediaAssets: Array<{ media: MediaMetadata; blobUrl: string }>,
  options: {
    totalDurationSeconds: number
    canvasWidth: number
    canvasHeight: number
    segmentWeights?: number[]
    segmentPlacements?: Array<{ startRatio: number; endRatio: number }>
  },
): Promise<{ inserted: boolean; itemIds: string[] }> {
  if (mediaAssets.length === 0) {
    return { inserted: false, itemIds: [] }
  }

  const timeline = useTimelineStore.getState()
  const targetTrack = findCompatibleTrackForItemType({
    tracks: timeline.tracks,
    items: timeline.items,
    itemType: 'video',
    preferredTrackId: null,
  })

  if (!targetTrack) {
    return { inserted: false, itemIds: [] }
  }

  const totalDurationFrames = Math.max(1, Math.round(options.totalDurationSeconds * timeline.fps))
  const desiredPosition = 0
  const finalPosition =
    findNearestAvailableSpace(
      desiredPosition,
      totalDurationFrames,
      targetTrack.id,
      timeline.items,
    ) ?? desiredPosition

  const segmentTimelinePlacements = (() => {
    const placements = Array.isArray(options.segmentPlacements)
      ? options.segmentPlacements
          .map((placement) => {
            const startRatio = Number(placement?.startRatio)
            const endRatio = Number(placement?.endRatio)
            if (!Number.isFinite(startRatio) || !Number.isFinite(endRatio)) {
              return null
            }

            const safeStartRatio = Math.min(Math.max(startRatio, 0), 1)
            const safeEndRatio = Math.min(Math.max(endRatio, safeStartRatio), 1)
            return {
              startRatio: safeStartRatio,
              endRatio: safeEndRatio,
            }
          })
          .filter((placement): placement is { startRatio: number; endRatio: number } => !!placement)
      : []

    if (placements.length === mediaAssets.length) {
      return placements.map((placement, index) => {
        const startFrame = Math.round(placement.startRatio * totalDurationFrames)
        const nextStartFrame =
          index === placements.length - 1
            ? totalDurationFrames
            : Math.round(
                (placements[index + 1]?.startRatio ?? placement.endRatio) * totalDurationFrames,
              )
        const rawEndFrame = Math.round(placement.endRatio * totalDurationFrames)
        const endFrame =
          index === placements.length - 1
            ? totalDurationFrames
            : Math.max(rawEndFrame, nextStartFrame)

        return {
          from: finalPosition + Math.min(startFrame, totalDurationFrames - 1),
          durationInFrames: Math.max(1, endFrame - startFrame),
        }
      })
    }

    return null
  })()

  const segmentDurationsInFrames = (() => {
    const weights = Array.isArray(options.segmentWeights)
      ? options.segmentWeights
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      : []

    if (weights.length !== mediaAssets.length) {
      const perItemFrames = Math.max(1, Math.floor(totalDurationFrames / mediaAssets.length))
      return mediaAssets.map((_, index) => {
        const isLast = index === mediaAssets.length - 1
        if (isLast) {
          return Math.max(1, totalDurationFrames - perItemFrames * index)
        }
        return perItemFrames
      })
    }

    const totalWeight = weights.reduce((sum, value) => sum + value, 0)
    if (!(totalWeight > 0)) {
      return mediaAssets.map(() =>
        Math.max(1, Math.floor(totalDurationFrames / mediaAssets.length)),
      )
    }

    const durations: number[] = []
    let previousBoundaryFrame = 0
    let cumulativeWeight = 0

    for (let index = 0; index < weights.length; index++) {
      cumulativeWeight += weights[index] ?? 0
      if (index === weights.length - 1) {
        durations.push(
          Math.max(
            1,
            finalPosition + totalDurationFrames - (finalPosition + previousBoundaryFrame),
          ),
        )
        continue
      }

      const targetBoundaryFrame = Math.round((cumulativeWeight / totalWeight) * totalDurationFrames)
      const durationInFrames = Math.max(1, targetBoundaryFrame - previousBoundaryFrame)
      durations.push(durationInFrames)
      previousBoundaryFrame += durationInFrames
    }

    return durations
  })()
  const createdIds: string[] = []
  let cursor = finalPosition

  for (let index = 0; index < mediaAssets.length; index++) {
    const asset = mediaAssets[index]
    if (!asset) {
      continue
    }
    const explicitPlacement = segmentTimelinePlacements?.[index]
    const itemFrom = explicitPlacement?.from ?? cursor
    const durationInFrames =
      explicitPlacement?.durationInFrames ??
      segmentDurationsInFrames[index] ??
      Math.max(1, finalPosition + totalDurationFrames - cursor)
    const mediaType = asset.media.mimeType.startsWith('video/') ? 'video' : 'image'
    const item = buildDroppedMediaTimelineItem({
      media: asset.media,
      mediaId: asset.media.id,
      mediaType,
      label: asset.media.fileName,
      timelineFps: timeline.fps,
      blobUrl: asset.blobUrl,
      canvasWidth: options.canvasWidth,
      canvasHeight: options.canvasHeight,
      placement: {
        trackId: targetTrack.id,
        from: itemFrom,
        durationInFrames,
      },
    }) as TimelineItem

    if (item.type === 'video') {
      item.embeddedAudioMuted = true
    }

    timeline.addItem(item)
    createdIds.push(item.id)
    cursor = itemFrom + durationInFrames
  }

  const insertedIds = createdIds.filter((itemId) =>
    useTimelineStore.getState().items.some((item) => item.id === itemId),
  )

  return {
    inserted: insertedIds.length === createdIds.length && insertedIds.length > 0,
    itemIds: insertedIds,
  }
}

export async function addDefaultTransitionsForVisualSequence(
  itemIds: string[],
  options?: { durationInFrames?: number },
): Promise<{ addedTransitionCount: number }> {
  if (itemIds.length < 2) {
    return { addedTransitionCount: 0 }
  }

  const timeline = useTimelineStore.getState()
  const durationInFrames = Math.max(6, options?.durationInFrames ?? Math.round(timeline.fps * 0.4))
  let addedTransitionCount = 0

  for (let index = 0; index < itemIds.length - 1; index++) {
    const leftItem = useTimelineStore.getState().items.find((item) => item.id === itemIds[index])
    const rightItem = useTimelineStore
      .getState()
      .items.find((item) => item.id === itemIds[index + 1])
    if (!leftItem || !rightItem) {
      continue
    }

    const validation = canAddTransition(leftItem, rightItem, durationInFrames, 0.5)
    if (!validation.canAdd) {
      continue
    }

    const inserted = useTimelineStore
      .getState()
      .addTransition(
        leftItem.id,
        rightItem.id,
        'crossfade',
        durationInFrames,
        'fade',
        undefined,
        0.5,
      )

    if (inserted) {
      addedTransitionCount += 1
    }
  }

  return { addedTransitionCount }
}

export function createAssistantTrack(input: {
  kind: 'video' | 'audio'
  name?: string
  position?: 'top' | 'bottom'
  locked?: boolean
  syncLock?: boolean
  visible?: boolean
  muted?: boolean
}): { trackId: string; kind: 'video' | 'audio'; name: string } {
  const timeline = useTimelineStore.getState()
  const kind = input.kind
  const position = input.position ?? 'bottom'

  // Determine order: top means insert at the top of same-kind tracks, bottom at the bottom
  const sameKindTracks = timeline.tracks.filter((t) => t.kind === kind)
  let order: number

  if (sameKindTracks.length === 0) {
    // No tracks of this kind exist — place at the end
    const maxOrder = timeline.tracks.reduce((max, t) => Math.max(max, t.order), -1)
    order = maxOrder + 1
  } else if (position === 'top') {
    const minOrder = Math.min(...sameKindTracks.map((t) => t.order))
    order = minOrder
    // Shift existing tracks of this kind down
    timeline.setTracks(
      timeline.tracks.map((t) =>
        t.kind === kind && t.order >= order ? { ...t, order: t.order + 1 } : t,
      ),
    )
  } else {
    const maxOrder = Math.max(...sameKindTracks.map((t) => t.order))
    order = maxOrder + 1
  }

  const newTrack = createClassicTrack({
    tracks: timeline.tracks,
    kind,
    order,
    height: undefined,
  })

  // Apply optional overrides
  if (input.name) {
    newTrack.name = input.name
  }
  if (input.locked !== undefined) {
    newTrack.locked = input.locked
  }
  if (input.syncLock !== undefined) {
    newTrack.syncLock = input.syncLock
  }
  if (input.visible !== undefined) {
    newTrack.visible = input.visible
  }
  if (input.muted !== undefined) {
    newTrack.muted = input.muted
  }

  timeline.setTracks([...timeline.tracks, newTrack])

  return { trackId: newTrack.id, kind: newTrack.kind!, name: newTrack.name }
}

function createBottomVideoTrack(tracks: ReturnType<typeof useTimelineStore.getState>['tracks']) {
  const bottomOrder = tracks.reduce((max, track) => Math.max(max, track.order), -1)
  return createClassicTrack({
    tracks,
    kind: 'video',
    order: bottomOrder + 1,
  })
}

function createTopVideoTrack(tracks: ReturnType<typeof useTimelineStore.getState>['tracks']) {
  const topOrder = tracks.reduce((min, track) => Math.min(min, track.order), 0)
  return createClassicTrack({
    tracks,
    kind: 'video',
    order: topOrder - 1,
  })
}

function trackContainsRenderableVisuals(
  trackId: string,
  items: readonly TimelineItem[],
): boolean {
  return items.some(
    (item) =>
      item.trackId === trackId &&
      (item.type === 'video' || item.type === 'image' || item.type === 'composition'),
  )
}

function trackHasOverlap(
  trackId: string,
  from: number,
  durationInFrames: number,
  items: readonly TimelineItem[],
): boolean {
  const end = from + durationInFrames
  return items.some((item) => {
    if (item.trackId !== trackId) return false
    const itemEnd = item.from + item.durationInFrames
    return item.from < end && itemEnd > from
  })
}

function resolveOverlayVideoTrack(params: {
  timeline: ReturnType<typeof useTimelineStore.getState>
  from: number
  durationInFrames: number
  trackId?: string
  createTrackIfNeeded: boolean
}): { id: string } | null {
  const { timeline, from, durationInFrames, trackId, createTrackIfNeeded } = params

  if (trackId) {
    return timeline.tracks.find((track) => track.id === trackId) ?? null
  }

  const videoTracks = [...timeline.tracks]
    .filter((track) => track.kind === 'video' && !track.isGroup && !track.locked && track.visible !== false)
    .sort((left, right) => left.order - right.order)

  const overlayTrack = videoTracks.find(
    (track) =>
      !trackContainsRenderableVisuals(track.id, timeline.items) &&
      videoTracks.some((candidate) => candidate.order > track.order),
  )
  if (
    overlayTrack &&
    !trackHasOverlap(overlayTrack.id, from, durationInFrames, timeline.items)
  ) {
    return overlayTrack
  }

  if (!createTrackIfNeeded) {
    return null
  }

  const newTrack = createTopVideoTrack(timeline.tracks)
  timeline.setTracks([...timeline.tracks, newTrack])
  return newTrack
}

export async function insertTextOverlay(input: {
  text: string
  fromSeconds: number
  durationSeconds: number
  trackId?: string
  createTrackIfNeeded?: boolean
  preset?: string
  style?: {
    fontFamily?: string
    fontSize?: number
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold'
    color?: string
    backgroundColor?: string
  }
}): Promise<{ itemId: string; trackId: string }> {
  const timeline = useTimelineStore.getState()
  const fps = timeline.fps || 30
  const createTrack = input.createTrackIfNeeded ?? true
  const canvas = useProjectStore.getState().currentProject?.metadata ?? { width: 1920, height: 1080 }

  const from = Math.round(input.fromSeconds * fps)
  const durationInFrames = Math.round(input.durationSeconds * fps)

  const targetTrack = resolveOverlayVideoTrack({
    timeline,
    from,
    durationInFrames,
    trackId: input.trackId,
    createTrackIfNeeded: createTrack,
  })

  if (!targetTrack) {
    throw new Error('No compatible overlay track found and createTrackIfNeeded is false')
  }

  const fontSize = input.style?.fontSize ?? Math.round(canvas.height * 0.06)
  const textItem: TextItem = {
    id: crypto.randomUUID(),
    type: 'text',
    trackId: targetTrack.id,
    from,
    durationInFrames,
    label: `Text: ${input.text.slice(0, 40)}`,
    text: input.text,
    color: input.style?.color ?? '#ffffff',
    fontSize,
    fontFamily: input.style?.fontFamily ?? 'Inter',
    fontWeight: input.style?.fontWeight ?? 'normal',
    backgroundColor: input.style?.backgroundColor ?? undefined,
    textAlign: 'center',
    lineHeight: 1.2,
    transform: {
      x: 0,
      y: 0,
      width: Math.round(canvas.width * 0.8),
      height: Math.round(fontSize * 2),
      rotation: 0,
      opacity: 1,
    },
  }

  timeline.addItem(textItem)

  const insertedTextItem = useTimelineStore.getState().items.find((item) => item.id === textItem.id)
  if (insertedTextItem) {
    return { itemId: insertedTextItem.id, trackId: insertedTextItem.trackId }
  }

  if (!input.trackId && createTrack) {
    const latestTracks = useTimelineStore.getState().tracks
    const fallbackTrack = createTopVideoTrack(latestTracks)
    useTimelineStore.getState().setTracks([...latestTracks, fallbackTrack])
    const fallbackTextItem: TextItem = {
      ...textItem,
      id: crypto.randomUUID(),
      trackId: fallbackTrack.id,
    }
    useTimelineStore.getState().addItem(fallbackTextItem)
    const insertedFallbackItem = useTimelineStore
      .getState()
      .items.find((item) => item.id === fallbackTextItem.id)
    if (insertedFallbackItem) {
      return { itemId: insertedFallbackItem.id, trackId: insertedFallbackItem.trackId }
    }
  }

  throw new Error('Failed to insert text overlay into the timeline.')
}

export async function insertImageOverlay(input: {
  mediaId: string
  fromSeconds: number
  durationSeconds: number
  trackId?: string
  createTrackIfNeeded?: boolean
  opacity?: number
  placement?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center'
}): Promise<{ itemId: string; trackId: string }> {
  const timeline = useTimelineStore.getState()
  const fps = timeline.fps || 30
  const createTrack = input.createTrackIfNeeded ?? true
  const canvas = useProjectStore.getState().currentProject?.metadata ?? { width: 1920, height: 1080 }
  const from = Math.round(input.fromSeconds * fps)
  const durationInFrames = Math.round(input.durationSeconds * fps)
  const media = await getMedia(input.mediaId)

  if (!media || !media.mimeType.startsWith('image/')) {
    throw new Error(`Image media not found: ${input.mediaId}`)
  }

  const resolvedBlobUrl = await mediaLibraryService.getMediaBlobUrl(input.mediaId)
  if (!resolvedBlobUrl) {
    throw new Error(`Unable to resolve image media blob: ${input.mediaId}`)
  }

  const targetTrack = resolveOverlayVideoTrack({
    timeline,
    from,
    durationInFrames,
    trackId: input.trackId,
    createTrackIfNeeded: createTrack,
  })

  if (!targetTrack) {
    throw new Error('No compatible overlay track found and createTrackIfNeeded is false')
  }

  // Compute placement transform
  const imgWidth = Math.round(canvas.width * 0.3)
  const imgHeight = Math.round(imgWidth * 0.5625)
  const margin = 20

  let tx: number, ty: number
  switch (input.placement ?? 'center') {
    case 'top-left':
      tx = -canvas.width / 2 + margin + imgWidth / 2
      ty = -canvas.height / 2 + margin + imgHeight / 2
      break
    case 'top-right':
      tx = canvas.width / 2 - margin - imgWidth / 2
      ty = -canvas.height / 2 + margin + imgHeight / 2
      break
    case 'bottom-left':
      tx = -canvas.width / 2 + margin + imgWidth / 2
      ty = canvas.height / 2 - margin - imgHeight / 2
      break
    case 'bottom-right':
      tx = canvas.width / 2 - margin - imgWidth / 2
      ty = canvas.height / 2 - margin - imgHeight / 2
      break
    case 'center':
    default:
      tx = 0
      ty = 0
      break
  }

  const imageItem = buildDroppedMediaTimelineItem({
    media,
    mediaId: media.id,
    mediaType: 'image',
    label: media.fileName,
    timelineFps: fps,
    blobUrl: resolvedBlobUrl,
    thumbnailUrl: resolvedBlobUrl,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    placement: {
      trackId: targetTrack.id,
      from,
      durationInFrames,
    },
  }) as ImageItem

  imageItem.transform = {
    x: Math.round(tx),
    y: Math.round(ty),
    width: imgWidth,
    height: imgHeight,
    rotation: 0,
    opacity: input.opacity ?? 1,
  }

  timeline.addItem(imageItem)

  const insertedImageItem = useTimelineStore
    .getState()
    .items.find((item) => item.id === imageItem.id)
  if (insertedImageItem) {
    return { itemId: insertedImageItem.id, trackId: insertedImageItem.trackId }
  }

  if (!input.trackId && createTrack) {
    const latestTracks = useTimelineStore.getState().tracks
    const fallbackTrack = createBottomVideoTrack(latestTracks)
    useTimelineStore.getState().setTracks([...latestTracks, fallbackTrack])
    const fallbackImageItem: ImageItem = {
      ...imageItem,
      id: crypto.randomUUID(),
      trackId: fallbackTrack.id,
    }
    useTimelineStore.getState().addItem(fallbackImageItem)
    const insertedFallbackItem = useTimelineStore
      .getState()
      .items.find((item) => item.id === fallbackImageItem.id)
    if (insertedFallbackItem) {
      return { itemId: insertedFallbackItem.id, trackId: insertedFallbackItem.trackId }
    }
  }

  throw new Error('Failed to insert image overlay into the timeline.')
}

export function setItemTransform(input: {
  itemId: string
  transform: {
    x?: number
    y?: number
    width?: number
    height?: number
    rotation?: number
    opacity?: number
  }
}): { updated: boolean } {
  const items = useItemsStore.getState().items
  const item = items.find((i) => i.id === input.itemId)

  if (!item) {
    return { updated: false }
  }

  useTimelineStore.getState().updateItemTransform(input.itemId, input.transform, {
    operation: 'transform',
  })

  return { updated: true }
}

export function addItemKeyframes(input: {
  itemId: string
  keyframes: Array<{
    property: string
    frame: number
    value: number
    easing?: string
  }>
}): { addedKeyframeCount: number } {
  const items = useItemsStore.getState().items
  const exists = items.some((i) => i.id === input.itemId)
  if (!exists) {
    return { addedKeyframeCount: 0 }
  }

  const addedKeyframeIds = useTimelineStore.getState().addKeyframes(
    input.keyframes.map((kf) => ({
      itemId: input.itemId,
      property: kf.property as never,
      frame: kf.frame,
      value: kf.value,
      easing: kf.easing as never,
    })),
  )

  return { addedKeyframeCount: addedKeyframeIds.length }
}

export function applyTransition(input: {
  leftClipId: string
  rightClipId: string
  type?: string
  presentation?: string
  durationInFrames?: number
  alignment?: number
}): { added: boolean; transitionId?: string } {
  const timeline = useTimelineStore.getState()
  const fps = timeline.fps || 30
  const duration = input.durationInFrames ?? Math.round(fps * 0.4)
  const transitionType = input.type ?? 'crossfade'
  const presentation = (input.presentation as 'fade' | 'slide' | 'push' | 'wipe' | undefined) ?? 'fade'
  const alignment = input.alignment ?? 0.5

  const inserted = timeline.addTransition(
    input.leftClipId,
    input.rightClipId,
    transitionType,
    duration,
    presentation,
    undefined,
    alignment,
  )

  if (!inserted) {
    return { added: false }
  }

  return { added: true, transitionId: `${input.leftClipId}-${input.rightClipId}` }
}

export function applyItemEffectPreset(input: {
  itemId: string
  presetId: string
}): { applied: boolean; effectCount: number } {
  const timeline = useTimelineStore.getState()
  const item = timeline.items.find((i) => i.id === input.itemId)
  if (!item) {
    return { applied: false, effectCount: 0 }
  }

  const preset = EFFECT_PRESETS.find((p) => p.id === input.presetId)
  if (!preset || preset.effects.length === 0) {
    return { applied: false, effectCount: 0 }
  }

  timeline.addEffects([{ itemId: input.itemId, effects: preset.effects }])

  return { applied: true, effectCount: preset.effects.length }
}

export function createMaskForItem(input: {
  targetItemId: string
  shapeType: 'rectangle' | 'circle' | 'ellipse' | 'path'
  maskType?: 'clip' | 'alpha'
  feather?: number
  invert?: boolean
}): { created: boolean } {
  const items = useItemsStore.getState().items
  const item = items.find((i) => i.id === input.targetItemId)
  if (!item) {
    return { created: false }
  }

  useItemsStore.getState()._updateItem(input.targetItemId, {
    maskType: input.maskType ?? 'alpha',
    maskFeather: input.feather ?? 10,
    maskInvert: input.invert ?? false,
  })

  return { created: true }
}

export function trimClip(input: {
  itemId: string
  startTrimFrames?: number
  endTrimFrames?: number
  ripple?: boolean
}): { trimmed: boolean } {
  const timeline = useTimelineStore.getState()
  const item = timeline.items.find((i) => i.id === input.itemId)
  if (!item) {
    return { trimmed: false }
  }

  let didTrim = false

  if (input.startTrimFrames !== undefined && input.startTrimFrames !== 0) {
    const startTrimDelta = input.startTrimFrames
    if (input.ripple) {
      timeline.rippleTrimItem(input.itemId, 'start', startTrimDelta)
    } else {
      timeline.trimItemStart(input.itemId, startTrimDelta)
    }
    didTrim = true
  }

  if (input.endTrimFrames !== undefined && input.endTrimFrames !== 0) {
    const endTrimDelta =
      input.endTrimFrames > 0 ? -input.endTrimFrames : input.endTrimFrames
    if (input.ripple) {
      timeline.rippleTrimItem(input.itemId, 'end', endTrimDelta)
    } else {
      timeline.trimItemEnd(input.itemId, endTrimDelta)
    }
    didTrim = true
  }

  return { trimmed: didTrim }
}

export function splitClip(input: {
  itemId: string
  splitFrame: number
  ripple?: boolean
}): { split: boolean; leftItemId: string | null; rightItemId: string | null } {
  const store = useItemsStore.getState()
  const item = store.items.find((i) => i.id === input.itemId)
  if (!item || input.splitFrame <= item.from || input.splitFrame >= item.from + item.durationInFrames) {
    return { split: false, leftItemId: null, rightItemId: null }
  }

  const result = store._splitItem(input.itemId, input.splitFrame)
  if (!result) {
    return { split: false, leftItemId: null, rightItemId: null }
  }

  // Ripple: close the gap left by splitting (inserts a 0-width gap between halves)
  if (input.ripple) {
    useItemsStore.getState()._closeGapAtPosition(result.rightItem.trackId, result.rightItem.from)
  }

  return {
    split: true,
    leftItemId: result.leftItem.id,
    rightItemId: result.rightItem.id,
  }
}

export function setAudioVolume(input: {
  itemId: string
  volumeDb: number
}): { updated: boolean } {
  const store = useItemsStore.getState()
  const item = store.items.find((i) => i.id === input.itemId)
  if (!item) return { updated: false }

  store._updateItem(input.itemId, { volume: input.volumeDb })
  return { updated: true }
}

export function setAudioFade(input: {
  itemId: string
  fadeInSeconds?: number
  fadeOutSeconds?: number
}): { updated: boolean } {
  const store = useItemsStore.getState()
  const item = store.items.find((i) => i.id === input.itemId)
  if (!item) return { updated: false }

  const updates: Record<string, number> = {}
  if (input.fadeInSeconds !== undefined) updates.fadeIn = input.fadeInSeconds
  if (input.fadeOutSeconds !== undefined) updates.fadeOut = input.fadeOutSeconds
  if (Object.keys(updates).length === 0) return { updated: false }

  store._updateItem(input.itemId, updates)
  return { updated: true }
}

export function duplicateAssistantItem(input: {
  itemId: string
  targetTrackId?: string
  fromSeconds?: number
}): { duplicated: boolean; newItemIds: string[] } {
  const store = useItemsStore.getState()
  const item = store.items.find((i) => i.id === input.itemId)
  if (!item) return { duplicated: false, newItemIds: [] }

  const from = input.fromSeconds !== undefined
    ? Math.round(input.fromSeconds * useTimelineStore.getState().fps)
    : item.from + item.durationInFrames + 30

  const targetTrackId = input.targetTrackId ?? item.trackId
  const newItems = store._duplicateItems([input.itemId], [{ from, trackId: targetTrackId }])

  return { duplicated: true, newItemIds: newItems.map((it) => it.id) }
}

export function rippleDeleteRange(input: {
  itemIds?: string[]
  trackId?: string
  rangeStartFrame?: number
  rangeEndFrame?: number
}): { deleted: boolean; deletedItemCount: number } {
  const store = useItemsStore.getState()

  let idsToDelete: string[] = []

  // Mode 1: explicit item IDs
  if (input.itemIds && input.itemIds.length > 0) {
    idsToDelete = input.itemIds.filter((id) => store.items.some((i) => i.id === id))
  }

  // Mode 2: track + frame range
  if (input.trackId && input.rangeStartFrame !== undefined && input.rangeEndFrame !== undefined) {
    const rangeStartFrame = input.rangeStartFrame
    const rangeEndFrame = input.rangeEndFrame
    idsToDelete = store.items
      .filter(
        (item) =>
          item.trackId === input.trackId &&
          item.from < rangeEndFrame &&
          item.from + item.durationInFrames > rangeStartFrame,
      )
      .map((item) => item.id)
  }

  if (idsToDelete.length === 0) return { deleted: false, deletedItemCount: 0 }

  // Use the store's private ripple delete method
  store._rippleDeleteItems(idsToDelete)
  return { deleted: true, deletedItemCount: idsToDelete.length }
}

export function setInOutPoints(input: {
  inPoint?: number | null
  outPoint?: number | null
  clear?: boolean
}): { updated: boolean } {
  if (input.clear) {
    useMarkersStore.getState().clearInOutPoints()
    return { updated: true }
  }

  let didUpdate = false
  if (input.inPoint !== undefined) {
    useMarkersStore.getState().setInPoint(input.inPoint ?? null)
    didUpdate = true
  }
  if (input.outPoint !== undefined) {
    useMarkersStore.getState().setOutPoint(input.outPoint ?? null)
    didUpdate = true
  }
  return { updated: didUpdate }
}

export function updateCaptionItems(input: {
  clipId: string
  captions: Array<{
    captionItemId: string
    text?: string
    fromSeconds?: number
    durationSeconds?: number
  }>
}): { updated: boolean; updatedItemCount: number } {
  const store = useItemsStore.getState()
  const fps = useTimelineStore.getState().fps
  let updatedCount = 0

  for (const cap of input.captions) {
    const existing = store.itemById[cap.captionItemId]
    if (!existing || existing.type !== 'text') continue

    const updates: Record<string, unknown> = {}
    if (cap.text !== undefined) updates.text = cap.text
    if (cap.fromSeconds !== undefined) updates.from = Math.round(cap.fromSeconds * fps)
    if (cap.durationSeconds !== undefined) updates.durationInFrames = Math.round(cap.durationSeconds * fps)
    if (Object.keys(updates).length === 0) continue

    store._updateItem(cap.captionItemId, updates)
    updatedCount++
  }

  return { updated: updatedCount > 0, updatedItemCount: updatedCount }
}

export async function freezeFrameAt(input: {
  itemId: string
  frame: number
}): Promise<{ inserted: boolean; freezeFrameItemId: string | null }> {
  const beforeIds = new Set(useItemsStore.getState().items.map((item) => item.id))
  const inserted = await insertFreezeFrame(input.itemId, input.frame)
  if (!inserted) {
    return { inserted: false, freezeFrameItemId: null }
  }

  const newItem = useItemsStore.getState().items.find(
    (item) => !beforeIds.has(item.id) && item.type === 'image',
  )
  return {
    inserted: true,
    freezeFrameItemId: newItem?.id ?? null,
  }
}
